/**
 * RelayBlade — right-anchored relay + schedule editor for the site dashboard.
 *
 * Replaces the full-screen EditSchedulesDialog + nested relay-settings dialog
 * with a single blade using the same chrome as CameraSchedulesBlade.
 * Supports editing ALL relays in one pass and committing with a single Save
 * (multi-relay batch via postSiteRelayConfig → POST /odl-app/site-relay-config/).
 *
 * Opening with a specific relayId focuses that relay's tab; opening with a
 * scheduleId additionally expands that schedule to edit mode. Users can still
 * switch to other relays before saving.
 *
 * Three sub-tabs per relay: Config (load type, voltage, etc.), Faces/Loads
 * (add/edit/delete/move relay loads), and Schedules. All changes batch into
 * one Save call.
 *
 * Data source: the odl-app unit snapshot via selectSiteUnit (Redux).
 * Write: postSiteRelayConfig (odl-app endpoint) — relay/load/schedule config and
 * any contract rows fold into one transactional call. The backend emits a 'unit'
 * socket event on success; no manual refresh needed.
 *
 * Contains:
 * - RelayBlade — the right-anchored Drawer (default export)
 */
import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, Button, Skeleton, Stack, Switch, Tab, Tabs, Typography } from '@mui/material';
import { parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Form, Formik } from 'formik';
import { produce } from 'immer';
import PropTypes from 'prop-types';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { postSiteRelayConfig } from '@sophocles/api/odlApp';
import ScopeContext from '@sophocles/app/ScopeContext';
import {
  relayConfigRequiresCommandLease,
  selectSiteFeatureAssociationByKey,
  selectSiteUnit,
} from '@sophocles/features/entities/site';
import {
  getLoadTypeCapabilities,
  selectDefaultLoadTypeIdForCustomerType,
  selectReferenceStatus,
  selectRelayLoadTypeCapsById,
} from '@sophocles/features/reference';
import { addToast } from '@sophocles/features/toast';
import { resolveScopeCustomerTypeId } from '@sophocles/hooks/relayLoadTerm';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';
import useSiteCommandWrite from '@sophocles/hooks/useSiteCommandWrite';
import { SMARTLINK_MODEL } from '@sophocles/utils';
import {
  SettingsFieldRow,
  SettingsSubsection,
  SettingsSwitchField,
  SettingsTextField,
} from '@sophocles/views/settings/components';
import PairRelayField from '@sophocles/views/sites/components/PairRelayField';
import PanelTypeField from '@sophocles/views/sites/components/PanelTypeField';
import RelayLoadForm from '@sophocles/views/sites/components/RelayCards/RelayLoadForm';
import ScheduleRow from '@sophocles/views/sites/components/RelayCards/ScheduleRow';
import DashboardBlade from '@sophocles/views/sites/components/SiteDashboard/DashboardBlade';
import {
  applyPanelTypeChange,
  applyRelayPairing,
  buildOffOffsetRule,
  buildOnOffsetRule,
  buildRelayInitialValues,
  canDiffRelayConfig,
  generateRelayConfigPayload,
} from '@sophocles/views/sites/relayConfigForm';

// ── Constants ─────────────────────────────────────────────────────────────────

const BLADE_WIDTH = { xs: '100%', sm: 640 };

// Sub-tab indices for the per-relay Config / Loads / Schedules strip.
const TAB_CONFIG = 0;
const TAB_LOADS = 1;
const TAB_SCHEDULES = 2;

const configPanelSx = {
  p: 'var(--space-3)',
};

const relayConfigSurfaceSx = {
  borderRadius: 'var(--radius-md)',
  border: 1,
  borderColor: 'divider',
  backgroundColor: 'background.paper',
  overflow: 'hidden',
  px: { xs: 1.5, sm: 2 },
  py: 0.5,
};

const bladeContentSx = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  bgcolor: 'var(--surface-page)',
};

const relayTabsRegionSx = {
  flexShrink: 0,
  bgcolor: 'background.paper',
  borderBottom: '1px solid var(--border-default)',
};

const relayTabsSx = {
  minHeight: 44,
  px: 'var(--page-gap)',
};

const relayTabSx = {
  minHeight: 44,
  minWidth: 96,
  px: 'var(--space-3)',
  fontWeight: 600,
  textTransform: 'none',
};

const areaTabsRegionSx = {
  flexShrink: 0,
  bgcolor: 'var(--surface-page)',
  px: 'var(--page-gap)',
  py: 'var(--space-2)',
};

const areaTabsSx = {
  minHeight: 42,
  '& .MuiTabs-indicator': { display: 'none' },
  '& .MuiTabs-flexContainer': {
    alignItems: 'stretch',
    gap: 'var(--space-2)',
  },
};

const areaTabSx = {
  minHeight: 42,
  minWidth: { xs: 128, sm: 0 },
  flex: { xs: '0 0 auto', sm: 1 },
  px: 'var(--space-3)',
  border: '1px solid var(--border-default)',
  borderRadius: 'calc(var(--radius-md) - 2px)',
  bgcolor: 'background.paper',
  color: 'text.secondary',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'var(--shadow-sm)',
  transition: 'all var(--duration-normal) var(--easing-standard)',
  '&:hover': {
    borderColor: 'var(--border-strong)',
    bgcolor: 'background.paper',
    boxShadow: 'var(--shadow-md)',
  },
  '&.Mui-selected': {
    color: 'primary.main',
    bgcolor: 'background.paper',
    borderColor: 'primary.main',
    boxShadow: 'inset 0 -3px 0 0 var(--md-primary), var(--shadow-md)',
    fontWeight: 700,
  },
};

const bladeBodySx = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  bgcolor: 'var(--surface-page)',
};

const loadsPanelSx = {
  minHeight: '100%',
  bgcolor: 'var(--surface-page)',
};

const loadActionBarSx = {
  px: 'var(--space-3)',
  pt: 'var(--space-3)',
  pb: 0,
};

const EMPTY_INITIAL_VALUES = { relayMonitor: { relaysEnabled: [] }, relays: [] };

function RelayBladeSkeleton() {
  return (
    <>
      <Box
        sx={{
          ...relayTabsRegionSx,
        }}
      >
        <Stack direction="row" spacing={1} sx={{ px: 'var(--page-gap)', py: 'var(--space-1)' }}>
          {[96, 96, 96].map((width, index) => (
            <Skeleton
              key={`relay-tab-skeleton-${index}`}
              variant="rectangular"
              width={width}
              height={32}
              sx={{ borderRadius: 'var(--radius-md)' }}
            />
          ))}
        </Stack>
      </Box>
      <Box sx={areaTabsRegionSx}>
        <Stack direction="row" spacing={1}>
          {[120, 80, 104].map((width, index) => (
            <Skeleton
              key={`relay-subtab-skeleton-${index}`}
              variant="rectangular"
              width={width}
              height={32}
              sx={{ borderRadius: 'var(--radius-md)' }}
            />
          ))}
        </Stack>
      </Box>
      <Box sx={{ ...bladeBodySx, p: 'var(--space-3)' }}>
        <Stack spacing={1.5}>
          {[64, 64, 64, 64, 64].map((height, index) => (
            <Skeleton
              key={`relay-field-skeleton-${index}`}
              variant="rectangular"
              height={height}
              sx={{ borderRadius: 'var(--radius-md)' }}
            />
          ))}
        </Stack>
      </Box>
    </>
  );
}

const resolveInitialTab = tab => {
  if (tab === 'loads') return TAB_LOADS;
  if (tab === 'schedules') return TAB_SCHEDULES;
  return TAB_CONFIG;
};

const resolveRelayTab = (tab, relay) => {
  if ((relay?.loads?.length ?? 0) === 0) return TAB_LOADS;
  return tab;
};

// ── Relay config panel (flat — no accordion) ──────────────────────────────────

/**
 * Flat relay configuration fields shown on the "Config" tab.
 * Voltage field notes: relay.voltage is the CONFIGURED site voltage that the
 * relay_actions payload sends as relayAction.voltage. It is different from the
 * read-only "last known voltage" monitoring metric. The form has always wired
 * it this way (see EditSchedulesDialog generatePayload); we preserve that contract.
 */
function RelayConfigPanel({
  relayMonitor,
  relay,
  relayIndex,
  relays,
  isBillboard,
  isCommercialUnit,
  isUnitDc,
  supportsRelayMonitor,
  supportsRelayPairing,
  defaultLoadTypeId,
  loadTypeCapsById,
  setFieldValue,
}) {
  const { t } = useTranslation();

  const getFieldName = field => `relays[${relayIndex}].${field}`;
  const relayCount = relays ? relays.filter(r => !r.hidden).length : 0;
  const selectedRelayNumbers = (relayMonitor?.relaysEnabled ?? []).map(Number);
  const isCurrentRelayMonitored = selectedRelayNumbers.includes(relay.relayNumber);
  const relayLoadTypeCaps = getLoadTypeCapabilities(loadTypeCapsById, relay.loadType);

  const handlePanelTypeChanged = e => {
    const nextRelays = applyPanelTypeChange(relays, relayIndex, e.target.value, {
      isCommercialUnit,
      defaultLoadTypeId,
      loadTypeCapsById,
    });
    if (nextRelays != null) setFieldValue('relays', nextRelays);
  };

  const handlePaired = (e, relayId) => {
    const nextRelays = applyRelayPairing(relays, relayIndex, relayId);
    if (nextRelays != null) setFieldValue('relays', nextRelays);
  };

  const toggleCurrentMonitorRelay = () => {
    const nextRelaysEnabled = isCurrentRelayMonitored
      ? selectedRelayNumbers.filter(num => num !== relay.relayNumber)
      : selectedRelayNumbers.concat(relay.relayNumber);

    setFieldValue(
      'relayMonitor.relaysEnabled',
      [...nextRelaysEnabled].map(Number).sort((a, b) => a - b),
    );
  };

  // Explicit unpair for a panel-type-paired relay (a supports_pairing load
  // type). Mirrors the un-latching in handlePanelTypeChanged's else branch,
  // and additionally reverts this relay's own load type (no panel-type
  // change drives it here).
  const handleUnpair = () => {
    const defaultLoadType = defaultLoadTypeId ?? (isCommercialUnit ? 6 : 1);
    const wasPairedType = relayLoadTypeCaps.supports_pairing;
    const pairedIdx = relays.findIndex(rv => rv.id === relay.secondaryId);
    setFieldValue(
      'relays',
      produce(relays, draft => {
        // Only revert the primary's load type when it was a paired/latching
        // type; a relay paired only via the secondary FK keeps its type.
        if (wasPairedType) draft[relayIndex].loadType = defaultLoadType;
        draft[relayIndex].relayFunction = 0;
        draft[relayIndex].secondaryId = 0;
        draft[relayIndex].secondaryNumber = null;
        if (pairedIdx !== -1) {
          draft[pairedIdx].hidden = false;
          draft[pairedIdx].relayFunction = 0;
          draft[pairedIdx].loadType = defaultLoadType;
          draft[pairedIdx].secondaryId = 0;
          draft[pairedIdx].secondaryNumber = null;
        }
      }),
    );
  };

  const isPaired = relay.secondaryId != null && relay.secondaryId !== 0;
  const isPairedRelayType = relayLoadTypeCaps.supports_pairing;
  const canConfigurePairing = !isUnitDc && !isPairedRelayType && supportsRelayPairing && relayCount > 1;
  const panelTypeLabel = isBillboard ? t('loadTypes.panelType') : t('loadTypes.switchType');

  return (
    <Stack direction="column" spacing={0} sx={configPanelSx}>
      <Box sx={relayConfigSurfaceSx}>
        <SettingsFieldRow label={panelTypeLabel} controlWidth={320} required divider>
          <PanelTypeField
            required
            fullWidth
            hiddenLabel
            name={getFieldName('loadType')}
            label={panelTypeLabel}
            size="small"
            variant="filled"
            onChange={handlePanelTypeChanged}
            isCommercialUnit={isCommercialUnit}
            isBillboard={isBillboard}
            relayNumber={relay.relayNumber}
          />
        </SettingsFieldRow>

        {relayLoadTypeCaps.is_triface_motor && (
          <SettingsFieldRow
            label={t('sites.trifaceMotorThreshold')}
            description={t('sites.trifaceMotorHelperText')}
            controlWidth={180}
            divider
          >
            <SettingsTextField name={getFieldName('threshold')} hiddenLabel size="small" fullWidth />
          </SettingsFieldRow>
        )}

        {isPairedRelayType && (
          <SettingsFieldRow
            label={t('sites.pulseTime')}
            description={t('sites.latchingContactorHelperText')}
            controlWidth={180}
            divider
          >
            <SettingsTextField name={getFieldName('pulseTime')} hiddenLabel size="small" fullWidth type="number" />
          </SettingsFieldRow>
        )}

        {isPaired && !canConfigurePairing && (
          <SettingsFieldRow
            label={t('sites.pairedRelay')}
            description={t('sites.pairedRelayDescription')}
            controlWidth={180}
            divider
          >
            <Button variant="outlined" color="warning" size="small" onClick={handleUnpair}>
              {relay.secondaryNumber != null
                ? t('sites.unpairRelayN', { n: relay.secondaryNumber, defaultValue: 'Unpair R{{n}}' })
                : t('sites.unpairRelay', 'Unpair')}
            </Button>
          </SettingsFieldRow>
        )}

        {canConfigurePairing && (
          <SettingsFieldRow
            label={t('sites.pairedRelay')}
            description={t('sites.pairedRelayDescription')}
            controlWidth={260}
            divider
          >
            <PairRelayField
              fullWidth
              hiddenLabel
              name={getFieldName('secondaryId')}
              label={t('sites.pairedRelay')}
              size="small"
              variant="filled"
              relayNumber={relay.relayNumber}
              secondaryId={relay.secondaryId}
              secondaryNumber={relay.secondaryNumber}
              relays={relays}
              onPaired={handlePaired}
            />
          </SettingsFieldRow>
        )}

        <SettingsFieldRow
          label={t('sites.voltage')}
          description={t('sites.voltageHelperText')}
          controlWidth={160}
          divider
        >
          <SettingsTextField
            name={getFieldName('voltage')}
            hiddenLabel
            size="small"
            type="number"
            fullWidth
            inputProps={{ min: 0, max: 1000, step: 0.1 }}
          />
        </SettingsFieldRow>

        {!isPairedRelayType && (
          <SettingsFieldRow
            label={t('sites.powerMeasurement')}
            description={t('sites.disablePowerMeasurement')}
            controlWidth={80}
            divider
          >
            <SettingsSwitchField name={getFieldName('powerMeasurementEnabled')} hideLabel />
          </SettingsFieldRow>
        )}

        {relayLoadTypeCaps.supports_normally_closed && (
          <SettingsFieldRow
            label={t('sites.normallyClosed')}
            description={t('sites.normallyClosedWarning')}
            controlWidth={80}
            divider
          >
            <SettingsSwitchField name={getFieldName('normallyClosed')} hideLabel />
          </SettingsFieldRow>
        )}

        <SettingsFieldRow label={t('sites.ignoreCharting', 'Ignore API Schedules')} controlWidth={80} divider={false}>
          <SettingsSwitchField name={getFieldName('ignoreCharting')} hideLabel />
        </SettingsFieldRow>
      </Box>

      {supportsRelayMonitor ? (
        <SettingsSubsection
          title={t('sites.relayMonitor', 'Relay Monitor')}
          description={t(
            'sites.relayMonitorDescription',
            'Choose which relays are monitored and how their contact state is interpreted.',
          )}
          bordered
        >
          <SettingsFieldRow
            label={t('sites.monitorRelay', 'Monitor Relay')}
            description={t('sites.monitorRelayDescription', 'Include this relay in relay monitor checks.')}
            controlWidth={80}
            divider={isCurrentRelayMonitored}
          >
            <Switch checked={isCurrentRelayMonitored} onChange={toggleCurrentMonitorRelay} />
          </SettingsFieldRow>
          {isCurrentRelayMonitored ? (
            <SettingsFieldRow
              label={t('sites.contactState', 'Contact State')}
              description={t('sites.contactStateDescription', 'Set the normal contact state for this relay.')}
              controlWidth={120}
              divider={false}
            >
              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                <SettingsSwitchField name={`relays[${relayIndex}].polarity`} hideLabel />
                <Typography variant="caption" color="text.secondary">
                  {relay.polarity
                    ? t('sites.normallyClosed', 'Normally Closed')
                    : t('sites.normallyOpenFull', 'Normally Open')}
                </Typography>
              </Stack>
            </SettingsFieldRow>
          ) : null}
        </SettingsSubsection>
      ) : null}
    </Stack>
  );
}

// ── RelayBlade ────────────────────────────────────────────────────────────────

/**
 * @param {object}          props
 * @param {boolean}         props.open
 * @param {Function}        props.onClose
 * @param {Function}        [props.onSaved]            - optional callback after a successful save
 * @param {number|string}   props.customerId
 * @param {number|string}   props.siteId
 * @param {number|string}   [props.siteGroupId] - needed to route to the calendar view when scheduling is calendar-managed
 * @param {number|null}     [props.initialRelayId]  - focus this relay on open
 * @param {number|null}     [props.initialScheduleId] - if set, open schedules tab
 * @param {'config'|'loads'|'schedules'} [props.initialTab] - tab to open when no schedule id is passed
 * @param {object|null}     [props.siteData]   - plain-JS site snapshot; when provided avoids .toJS() selector
 * @param {boolean}         [props.isBillboardOverride] - isBillboard from the parent; avoids customer .toJS()
 */
export default function RelayBlade({
  open,
  onClose,
  onSaved,
  customerId,
  siteId,
  siteGroupId = null,
  initialRelayId = null,
  initialScheduleId = null,
  initialTab = 'config',
  siteData = null,
  isBillboardOverride = null,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ── Redux selectors ───────────────────────────────────────────────────────

  // Prefer props injected from SiteDashboardView (plain-JS snapshot) to avoid
  // the .toJS() allocation-per-dispatch anti-pattern on a socket-joined page.
  // Fall back to the entity store reads for callers that don't pass the props yet.
  const customerFromStore = useSelector(state =>
    isBillboardOverride === null ? state.entities.customers.toJS()[customerId] : null,
  );
  const isBillboard = isBillboardOverride !== null ? isBillboardOverride : (customerFromStore?.is_billboard ?? false);

  // Customer-type relay-load term ("Face"/"Zone"/"Panel") for the loads tab and
  // add-load button; i18n default ("Face"/"Faces") outside a term'd customer.
  const relayLoadTerm = useRelayLoadTerm();

  // The in-scope customer's CustomerType default load-type id, preselected for
  // relays with no saved load_type_id (new relay/unlatch flows).
  const scope = useContext(ScopeContext);
  const customerTypeId = resolveScopeCustomerTypeId(scope);
  const defaultLoadTypeId = useSelector(selectDefaultLoadTypeIdForCustomerType(customerTypeId));

  // RelayLoadType capability flags, keyed by id — drives every load-type KIND
  // check below (fixtures, normally-closed, triface threshold, pairing) that
  // used to hardcode a catalog id.
  const loadTypeCapsById = useSelector(selectRelayLoadTypeCapsById);

  // The reference catalog (relay load types, incl. capability flags) is
  // dispatched non-blocking at app boot with no route gating — a relay blade
  // can mount and open before it resolves. Form init below waits for it so
  // the initial values are never built against an empty caps map.
  const { loaded: referenceLoaded } = useSelector(selectReferenceStatus);

  // Site time zone and sun times come from the snapshot site data (entities.sites
  // is seeded from the snapshot by SiteDashboardView's useEffect).
  const siteFromStore = useSelector(state => (siteData === null ? state.entities.sites.toJS()[siteId] : null));
  const site = siteData ?? siteFromStore;
  const timeZone = site?.time_zone ?? 'UTC';

  const dimmingFeature = useSelector(selectSiteFeatureAssociationByKey(siteId, 'DIMMING'));
  const calendarFeature = useSelector(selectSiteFeatureAssociationByKey(siteId, 'CALENDAR'));
  const siteUsesCalendar = calendarFeature?.is_effectively_enabled ?? false;

  // Unit from the odl-app snapshot — the single source of truth for relay data.
  const unit = useSelector(selectSiteUnit(siteId));
  const { commandLocked, runWrite } = useSiteCommandWrite(siteId);

  const fw = unit?.firmware_version ?? null;
  const supportsNegativeOffsets = fw?.supports_negative_schedule_offsets ?? false;
  const supportsRelayMonitor = fw?.supports_relay_monitor === true;
  const maxSchedules = fw?.schedules_per_relay ?? 2;
  const dayOfWeekScheduling = fw != null ? (fw.supports_day_of_week_schedules ?? false) : isBillboard === false;

  const nextSunset = site?.next_sunset ? toZonedTime(parseISO(site.next_sunset), timeZone) : null;
  const nextSunrise = site?.next_sunrise ? toZonedTime(parseISO(site.next_sunrise), timeZone) : null;

  // Derive unit-level flags from snapshot.
  const modelId = unit?.model?.id ?? 0;
  const isCommercialUnit = fw != null ? unit?.uses_zoneinfo_status === true : !isBillboard;
  const isUnitDc = [
    SMARTLINK_MODEL.SL_DC_WITHOUT_DOW,
    SMARTLINK_MODEL.SL_DC_WITH_DOW,
    SMARTLINK_MODEL.SL_TWO_PORT_DC,
  ].includes(modelId);

  const supportsRelayPairing = unit?.model?.supports_relay_pairing === true;
  const dimmerAvailable = modelId === SMARTLINK_MODEL.SL_TWO_PORT_DC || dimmingFeature?.is_enabled;

  // Relay list from snapshot — plain JS objects with nested loads/schedules.
  const snapshotRelays = unit?.relays ?? [];

  // ── Local state ───────────────────────────────────────────────────────────

  // relay_function / relay_pulse_time come from the snapshot configuration block.
  const configuration = unit?.configuration ?? null;

  const [isInitialized, setIsInitialized] = useState(false);
  const [initialValues, setInitialValues] = useState(EMPTY_INITIAL_VALUES);
  // activeRelayIndex: which relay tab is focused
  const [activeRelayIndex, setActiveRelayIndex] = useState(0);
  // activeTab: 0 = Config, 1 = Loads/Faces, 2 = Schedules
  const [activeTab, setActiveTab] = useState(TAB_CONFIG);
  const [isSaving, setIsSaving] = useState(false);

  // ── Form initialization ───────────────────────────────────────────────────

  useEffect(() => {
    if (!open) {
      setIsInitialized(false);
      return;
    }
    // Ready when isBillboard is known: either the parent passed the override, or
    // the customer entity has loaded from the store. Also wait for the reference
    // catalog so buildRelayInitialValues sees real load-type capability flags
    // instead of the empty-map DEFAULT_LOAD_TYPE_CAPS fallback.
    if ((isBillboardOverride === null && customerFromStore == null) || snapshotRelays.length === 0) return;
    if (!referenceLoaded) return;
    if (isInitialized && snapshotRelays.length === initialValues.relays.length) return;

    setInitialValues({
      relayMonitor: {
        relaysEnabled: (unit?.relay_monitor?.enabled ? unit?.relay_monitor?.relays_enabled : []) ?? [],
      },
      relays: buildRelayInitialValues(snapshotRelays, {
        configuration,
        isCommercialUnit,
        supportsNegativeOffsets,
        maxSchedules,
        includePolarity: true,
        defaultLoadTypeId,
        loadTypeCapsById,
      }),
    });
    setIsInitialized(true);
  }, [
    open,
    isInitialized,
    customerFromStore,
    isBillboardOverride,
    snapshotRelays.length,
    configuration,
    defaultLoadTypeId,
    loadTypeCapsById,
    referenceLoaded,
  ]);

  // ── Focus effect: jump to the right relay+tab when opened from a cog ─────

  useEffect(() => {
    if (!open || !isInitialized) return;

    if (initialRelayId != null) {
      const idx = initialValues.relays.reduce((found, r, i) => (r.id === initialRelayId ? i : found), 0);
      const tab = initialScheduleId != null ? TAB_SCHEDULES : resolveInitialTab(initialTab);
      setActiveRelayIndex(idx);
      setActiveTab(resolveRelayTab(tab, initialValues.relays[idx]));
    } else {
      const tab = resolveInitialTab(initialTab);
      setActiveRelayIndex(0);
      setActiveTab(resolveRelayTab(tab, initialValues.relays[0]));
    }
  }, [open, isInitialized, initialRelayId, initialScheduleId, initialTab]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleClose = () => {
    setInitialValues(EMPTY_INITIAL_VALUES);
    setIsInitialized(false);
    onClose();
  };

  const handleSubmit = async values => {
    const { configPayload, contractRows } = generateRelayConfigPayload(values, initialValues, {
      supportsNegativeOffsets,
      loadTypeCapsById,
    });
    const requiresCommand = relayConfigRequiresCommandLease(configPayload, { hasRadio: unit?.radio != null });
    setIsSaving(true);

    // Contracts fold into the config write so the whole change is one
    // transactional call and a rolled-back write never emits a phantom unit.
    const payload = contractRows.length > 0 ? { ...configPayload, contracts: contractRows } : configPayload;

    try {
      const outcome = await runWrite(() => postSiteRelayConfig(siteId, payload), { requiresCommand });
      if (!outcome.executed) return;
      // The backend emits a 'unit' socket event; the snapshot updates automatically.
      if (onSaved) onSaved();
      handleClose();
    } catch (err) {
      console.error('RelayBlade: save failed', err);
      const detail = err?.response?.data?.errors?.[0]?.detail;
      dispatch(
        addToast({
          message: detail || t('sites.relaySaveFailed', 'Failed to save relay configuration. Please try again.'),
          level: 'error',
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const determineActiveSchedulesHaveDaysOfWeek = values =>
    values.relays.every(r => r.schedules.every(s => !s.enabled || (s.enabled && s.daysOfWeek?.length > 0)));

  // ── Render ────────────────────────────────────────────────────────────────

  const validationSchema = yup.object().shape({
    relays: yup.array().of(
      yup.object({
        voltage: yup.number().min(0, t('validation.min0')).max(480, t('validation.voltageRange')),
        loads: yup.array().of(
          yup.object().shape({
            name: yup
              .string(t('validation.enterName'))
              .max(64, t('validation.nameTooLong'))
              .required(t('validation.nameRequired')),
            description: yup.string(t('validation.enterDescription')).max(100, t('validation.descTooLong')),
          }),
        ),
        schedulesDisabled: yup.boolean(),
        schedules: yup.array().of(
          yup.object().shape({
            enabled: yup.boolean(),
            onType: yup.string().oneOf(['sunset', 'sunrise', 'time']),
            offType: yup.string().oneOf(['sunset', 'sunrise', 'time']),
            onOffset: yup.mixed().when('onType', {
              is: type => type === 'sunset' || type === 'sunrise',
              then: () => buildOnOffsetRule(supportsNegativeOffsets, t),
              otherwise: () => yup.number().nullable(),
            }),
            offOffset: yup.mixed().when('offType', {
              is: type => type === 'sunset' || type === 'sunrise',
              then: () => buildOffOffsetRule(supportsNegativeOffsets, t),
              otherwise: () => yup.number().nullable(),
            }),
            isAlwaysOn: yup.boolean(),
            daysOfWeek: yup.array().of(yup.number()),
            dutyCycle: yup.number().integer().min(0, t('validation.min0')).max(100, t('validation.dutyCycleRange')),
            frequency: yup.number().integer().is([1, 5], t('validation.frequencyMustBe1Or5')),
          }),
        ),
      }),
    ),
  });

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, isValid, dirty, submitForm, setFieldValue, resetForm }) => {
        const allActiveSchedulesHaveDaysOfWeek = determineActiveSchedulesHaveDaysOfWeek(values);
        const relaysInSync = canDiffRelayConfig(values, initialValues);
        const pendingWrite = relaysInSync
          ? generateRelayConfigPayload(values, initialValues, {
              supportsNegativeOffsets,
              loadTypeCapsById,
            })
          : null;
        const saveRequiresCommand = relaysInSync
          ? relayConfigRequiresCommandLease(pendingWrite.configPayload, {
              hasRadio: unit?.radio != null,
            })
          : false;
        const canSave =
          isInitialized &&
          relaysInSync &&
          isValid &&
          dirty &&
          allActiveSchedulesHaveDaysOfWeek &&
          !isSaving &&
          !(commandLocked && saveRequiresCommand);

        const visibleFromValues = values.relays.filter(r => !r.hidden);
        const currentRelay = visibleFromValues[activeRelayIndex] ?? visibleFromValues[0];
        const currentRelayIndex = values.relays.findIndex(r => r.id === currentRelay?.id);
        const currentRelayHasLoads = (currentRelay?.loads?.length ?? 0) > 0;
        const effectiveActiveTab = resolveRelayTab(activeTab, currentRelay);
        const loadEntityLabel = relayLoadTerm.singular;
        const handleBladeClose = () => {
          resetForm();
          handleClose();
        };
        const saveAction = (
          <Button variant="contained" size="small" disabled={!canSave} onClick={submitForm} sx={{ flexShrink: 0 }}>
            {t('actions.save')}
          </Button>
        );

        return (
          <Form>
            <DashboardBlade
              open={open}
              onClose={handleBladeClose}
              width={BLADE_WIDTH}
              title={t('sites.relaySettings', 'Relay Settings')}
              headerAction={saveAction}
            >
              <Box sx={bladeContentSx}>
                {!isInitialized ? (
                  <RelayBladeSkeleton />
                ) : (
                  <>
                    {/* Relay tabs (one tab per visible relay) — driven by live
                      Formik values so pairing/unpairing adds/removes a tab. */}
                    {visibleFromValues.length > 1 && (
                      <Box sx={relayTabsRegionSx}>
                        <Tabs
                          value={activeRelayIndex}
                          onChange={(e, v) => {
                            setActiveRelayIndex(v);
                            setActiveTab(resolveRelayTab(effectiveActiveTab, visibleFromValues[v]));
                          }}
                          sx={relayTabsSx}
                          variant="scrollable"
                          scrollButtons="auto"
                          allowScrollButtonsMobile
                          aria-label={t('sites.relays', 'Relays')}
                        >
                          {visibleFromValues.map(r => (
                            <Tab
                              key={r.id}
                              label={
                                `${t('sites.relay', 'Relay')} ${r.relayNumber}` +
                                (r.secondaryId && r.secondaryId !== 0 ? `+${r.secondaryNumber}` : '')
                              }
                              sx={relayTabSx}
                            />
                          ))}
                        </Tabs>
                      </Box>
                    )}

                    {/* Config / Loads / Schedules sub-tabs for the focused relay */}
                    <Box sx={areaTabsRegionSx}>
                      <Tabs
                        value={effectiveActiveTab}
                        onChange={(e, v) => {
                          if (!currentRelayHasLoads && v !== TAB_LOADS) return;
                          setActiveTab(v);
                        }}
                        sx={areaTabsSx}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        aria-label={t('sites.relaySettings', 'Relay Settings')}
                      >
                        <Tab
                          label={t('general.configuration', 'Configuration')}
                          disabled={!currentRelayHasLoads}
                          sx={areaTabSx}
                        />
                        <Tab label={relayLoadTerm.plural} sx={areaTabSx} />
                        <Tab
                          label={t('general.schedules', 'Schedules')}
                          disabled={!currentRelayHasLoads}
                          sx={areaTabSx}
                        />
                      </Tabs>
                    </Box>

                    {/* Body */}
                    <Box sx={bladeBodySx}>
                      {currentRelay == null ? null : effectiveActiveTab === TAB_CONFIG ? (
                        /* Config tab */
                        <RelayConfigPanel
                          relayMonitor={values.relayMonitor}
                          relay={currentRelay}
                          relayIndex={currentRelayIndex}
                          relays={values.relays}
                          isBillboard={isBillboard}
                          isCommercialUnit={isCommercialUnit}
                          isUnitDc={isUnitDc}
                          supportsRelayMonitor={supportsRelayMonitor}
                          supportsRelayPairing={supportsRelayPairing}
                          defaultLoadTypeId={defaultLoadTypeId}
                          loadTypeCapsById={loadTypeCapsById}
                          setFieldValue={setFieldValue}
                        />
                      ) : effectiveActiveTab === TAB_LOADS ? (
                        /* Loads / Faces tab */
                        <Box sx={loadsPanelSx}>
                          {/* Add face/zone button */}
                          <Stack direction="row" alignItems="center" sx={loadActionBarSx}>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<AddIcon fontSize="small" />}
                              onClick={() => {
                                setActiveTab(TAB_LOADS);
                                setFieldValue(
                                  'relays',
                                  produce(values.relays, draft => {
                                    draft[currentRelayIndex].loads.push({
                                      id: null,
                                      name: `${loadEntityLabel} ${values.relays[currentRelayIndex].loads.length + 1}`,
                                      description: '',
                                      direction: '',
                                      lightFixture: 1,
                                      fixtureQuantity: 2,
                                      hasFixture: getLoadTypeCapabilities(
                                        loadTypeCapsById,
                                        values.relays[currentRelayIndex].loadType,
                                      ).supports_light_fixtures,
                                      isOpen: true,
                                      contract: null,
                                    });
                                  }),
                                );
                              }}
                            >
                              {t('actions.add', 'Add')} {loadEntityLabel}
                            </Button>
                          </Stack>
                          {/* Load rows */}
                          {values.relays[currentRelayIndex]?.loads.map((l, k) => (
                            <Fragment key={`load-${l.id ?? k}`}>
                              <RelayLoadForm
                                relay={values.relays[currentRelayIndex]}
                                load={l}
                                site={site}
                                namePath={`relays[${currentRelayIndex}].loads[${k}]`}
                                availableRelays={values.relays
                                  .filter(
                                    r =>
                                      !r.loads.map(rl => rl.id).includes(values.relays[currentRelayIndex].loads[k].id),
                                  )
                                  .map(r => ({ id: r.id, number: r.relayNumber }))}
                                onMove={targetRelayId => {
                                  const targetIdx = values.relays.findIndex(r => r.id === targetRelayId);
                                  if (targetIdx !== -1) {
                                    setFieldValue(
                                      'relays',
                                      produce(values.relays, draft => {
                                        draft[targetIdx].loads.push(draft[currentRelayIndex].loads[k]);
                                        draft[currentRelayIndex].loads.splice(k, 1);
                                      }),
                                    );
                                  }
                                }}
                                onDelete={() =>
                                  setFieldValue(
                                    'relays',
                                    produce(values.relays, draft => {
                                      draft[currentRelayIndex].loads.splice(k, 1);
                                    }),
                                  )
                                }
                              />
                            </Fragment>
                          ))}
                        </Box>
                      ) : currentRelay?.normallyClosed ? (
                        /* Schedules tab, normally-closed relay: a normally-closed
                          relay is always powered, so scheduling does not apply and
                          the editor is suppressed. Turning Normally Closed off on
                          the Config tab re-enables the schedule editor live. */
                        <Box sx={{ p: 'var(--space-3)' }}>
                          <Alert severity="info">
                            {t(
                              'sites.normallyClosedSchedulesDisabledNotice',
                              'Normally closed relays are always powered, so scheduling is disabled. Turn off Normally Closed to configure schedules.',
                            )}
                          </Alert>
                        </Box>
                      ) : siteUsesCalendar ? (
                        /* Schedules tab, calendar-managed site: relay schedules are
                          written by calendar events, not edited here. */
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ p: 'var(--space-3)' }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ textTransform: 'uppercase', color: theme => theme.palette.warning.main }}
                          >
                            {t('sites.schedulingManagedViaCalendar')}
                          </Typography>
                          <Button
                            onClick={() =>
                              navigate(`/c/${customerId}/plant/${siteGroupId}/structure/${siteId}/calendar`)
                            }
                          >
                            {t('general.calendar')}
                          </Button>
                        </Stack>
                      ) : (
                        /* Schedules tab */
                        <Box>
                          {values.relays[currentRelayIndex]?.schedules.map((s, si) => (
                            <Fragment key={`schedule-${s.id ?? si}`}>
                              <ScheduleRow
                                title={`${t('general.schedule', 'Schedule')} ${s.scheduleNumber}`}
                                schedule={s}
                                namePath={`relays[${currentRelayIndex}].schedules[${si}]`}
                                dimmerAvailable={dimmerAvailable}
                                is2PDC={isUnitDc}
                                supportsDayOfWeek={dayOfWeekScheduling}
                                supportsNegativeOffsets={supportsNegativeOffsets}
                                showAlwaysOn
                                nextSunset={nextSunset}
                                nextSunrise={nextSunrise}
                              />
                            </Fragment>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </DashboardBlade>
          </Form>
        );
      }}
    </Formik>
  );
}

RelayBlade.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  siteGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialRelayId: PropTypes.number,
  initialScheduleId: PropTypes.number,
  initialTab: PropTypes.oneOf(['config', 'loads', 'schedules']),
};
